import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, X, Crown, Eye } from 'lucide-react';

const PLAN_ORDER = ['free', 'basic', 'premium', 'premium_max', 'premium_ultra'];
const PLAN_LABELS = {
  free: { lv: 'Sākuma', en: 'Starter' }, basic: { lv: 'Basic', en: 'Basic' },
  premium: { lv: 'Premium', en: 'Premium' }, premium_max: { lv: 'Premium Max', en: 'Premium Max' },
  premium_ultra: { lv: 'Premium Ultra', en: 'Premium Ultra' },
};

// ✓/✗ matrix. Value: true | false | string (e.g. image limit).
const FEATURE_MATRIX = [
  { label: { lv: 'Krāsas, fonti, izkārtojums', en: 'Colors, fonts, layout' }, vals: [false, true, true, true, true] },
  { label: { lv: 'Custom CSS (drošs)', en: 'Custom CSS (sandboxed)' }, vals: [false, true, true, true, true] },
  { label: { lv: 'Gatavie šabloni + baneri', en: 'Templates + banners' }, vals: [false, false, true, true, true] },
  { label: { lv: 'Melnraksts / Preview / Undo', en: 'Draft / Preview / Undo' }, vals: [false, false, false, true, true] },
  { label: { lv: 'Dizains publiskajā profilā', en: 'Design on public profile' }, vals: [false, false, false, true, true] },
  { label: { lv: 'Headless API', en: 'Headless API' }, vals: [false, false, false, false, true] },
  { label: { lv: 'Tēmu dzinējs (Liquid / Jinja2)', en: 'Theme engine (Liquid / Jinja2)' }, vals: [false, false, false, false, true] },
  { label: { lv: 'Webhooks + piegādes žurnāls', en: 'Webhooks + delivery log' }, vals: [false, false, false, false, true] },
  { label: { lv: 'Bilžu limits', en: 'Image limit' }, vals: ['5', '12', '20', '25', '30'] },
];

const INSTRUCTIONS = {
  basic: {
    lv: ['Atver sadaļu "Veikala dizains" savā veikala profilā.', 'Izvēlies krāsu paleti un fontu — izmaiņas redzamas uzreiz.', 'Pielāgo hero stilu un produktu izkārtojumu.', '(Pēc izvēles) Pievieno savu CSS lauciņā "Custom CSS".', 'Nospied "Saglabāt".'],
    en: ['Open "Shop design" in your shop profile.', 'Pick a palette and font — changes apply instantly.', 'Adjust hero style and product layout.', '(Optional) Add your own CSS in the "Custom CSS" field.', 'Click "Save".'],
  },
  premium: {
    lv: ['Visi Basic soļi pieejami.', 'Sadaļā "Šabloni" izvēlies vienu no 5 gataviem šabloniem.', 'Pievieno HTML banneri veikala augšā/apakšā (saturs tiek droši filtrēts).', 'Saglabā un apskati savu publisko veikalu.'],
    en: ['All Basic steps available.', 'In "Templates" pick one of 5 ready templates.', 'Add an HTML banner at the top/bottom (safely sanitized).', 'Save and view your public shop.'],
  },
  premium_max: {
    lv: ['Veic dizaina izmaiņas un nospied "Saglabāt kā melnrakstu" — tās vēl nav redzamas pircējiem.', 'Priekšskati melnrakstu, izmanto "Atsaukt" kļūdām.', 'Kad gatavs — nospied "Publicēt", lai dizains kļūst publisks.', 'Ieslēdz "Lietot arī publiskajam profilam", ja vēlies.'],
    en: ['Make design changes and click "Save as draft" — not visible to buyers yet.', 'Preview the draft, use "Undo" for mistakes.', 'When ready, click "Publish" to go live.', 'Toggle "Apply to public profile" if desired.'],
  },
  premium_ultra: {
    lv: [
      'Headless API: sadaļā "Headless API" ģenerē API atslēgu un izmanto to galvenē X-API-Key. Lejuplādē Next.js/Vue/WordPress starter komplektu no /developers.',
      'Tēmu dzinējs: izvēlies dzinēju (Liquid vai Jinja2), ielādē paraugu, raksti tēmu ar reāllaika priekšskatījumu un ierīču skatu, tad publicē.',
      'Webhooks: ievadi sava servera URL, izvēlies notikumus (order.paid, order.shipped, inventory.low), saglabā. Pārbaudi ar pogu "Tests".',
      'Neizdevušās webhook piegādes atkārtojas automātiski; skaties piegādes žurnālā un lejuplādē CSV.',
    ],
    en: [
      'Headless API: in "Headless API" generate a key and send it in the X-API-Key header. Download a Next.js/Vue/WordPress starter from /developers.',
      'Theme engine: pick an engine (Liquid or Jinja2), load a starter, edit with live preview and device views, then publish.',
      'Webhooks: enter your server URL, choose events (order.paid, order.shipped, inventory.low), save. Test with the "Test" button.',
      'Failed webhook deliveries auto-retry; check the delivery log and export CSV.',
    ],
  },
};

export default function PlansModal({ open, onOpenChange, plans, active, planId, walletBalance, subscribing, onSubscribe, onWalletSubscribe, language, promo }) {
  const [tab, setTab] = useState('plans');
  const tr = (lv, en) => (language === 'lv' ? lv : en);
  const currentId = active ? planId : 'free';

  const tabs = [
    ['plans', tr('Plāni', 'Plans')],
    ['compare', tr('Salīdzinājums', 'Comparison')],
    ['guide', tr('Instrukcijas', 'How-to')],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="plans-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl">{tr('KoLV Premium plāni', 'KoLV Premium plans')}</DialogTitle>
        </DialogHeader>

        {promo && (
          <div className="rounded-xl bg-gradient-to-r from-[#D97736] to-[#4A6B53] text-white px-4 py-2.5 mb-2 flex items-center gap-2" data-testid="launch-promo-banner">
            <Crown className="w-4 h-4 shrink-0" />
            <span className="text-sm font-semibold">{tr('🎉 Launch akcija — visi plāni šobrīd BEZMAKSAS!', '🎉 Launch offer — all plans are FREE right now!')}</span>
          </div>
        )}

        <div className="flex gap-1.5 border-b border-[#F3F2EE] mb-3">
          {tabs.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === id ? 'border-[#4A6B53] text-[#4A6B53]' : 'border-transparent text-[#5C5C5C] hover:text-[#121212]'}`}
              data-testid={`plans-tab-${id}`}>{label}</button>
          ))}
        </div>

        {tab === 'plans' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="plans-tab-content">
            {plans.map((p) => {
              const isCurrent = currentId === p.id;
              return (
                <div key={p.id} className={`p-4 border-2 rounded-xl relative flex flex-col ${isCurrent ? 'border-[#4A6B53] bg-[#4A6B53]/5' : p.popular ? 'border-[#D97736]' : 'border-[#E5E4E0]'}`} data-testid={`modal-plan-${p.id}`}>
                  {p.popular && !isCurrent && <span className="absolute -top-2.5 right-3 bg-[#D97736] text-white text-[10px] px-2 py-0.5 rounded-full">{tr('POPULĀRS', 'POPULAR')}</span>}
                  {isCurrent && <span className="absolute -top-2.5 right-3 bg-[#4A6B53] text-white text-[10px] px-2 py-0.5 rounded-full">{tr('AKTĪVS', 'ACTIVE')}</span>}
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-[#121212] flex items-center gap-1">{p.id !== 'free' && <Crown className="w-3.5 h-3.5 text-[#D97736]" />}{p.id === 'free' ? tr('Sākuma', 'Starter') : p.name}</h4>
                    <span className="font-outfit font-bold text-[#D97736] text-sm">{p.price === 0 ? tr('Bezmaksas', 'Free') : (promo ? <span className="flex items-baseline gap-1"><s className="text-[#B0AFAB] font-normal text-xs">€{p.price}</s><span className="text-[#4A6B53]">{tr('Bezmaksas', 'Free')}</span></span> : <>€{p.price}<span className="text-[10px] font-normal text-[#5C5C5C]">/{tr('gadā', 'yr')}</span></>)}</span>
                  </div>
                  <ul className="text-xs text-[#5C5C5C] space-y-1 mb-3 flex-1">
                    {p.feats[language === 'lv' ? 'lv' : 'en'].map((f, i) => (
                      <li key={i} className="flex items-start gap-1"><Check className="w-3 h-3 text-green-600 mt-0.5 shrink-0" /> {f}</li>
                    ))}
                  </ul>
                  {p.id !== 'free' && !isCurrent && (
                    promo ? (
                      <Button onClick={() => onSubscribe(p.id)} disabled={subscribing} className="w-full bg-[#4A6B53] hover:bg-[#3B5643] text-white text-xs h-9" data-testid={`modal-subscribe-${p.id}`}>
                        {tr('Aktivizēt bez maksas', 'Activate for free')}
                      </Button>
                    ) : (
                    <div className="space-y-2">
                      <Button onClick={() => onSubscribe(p.id)} disabled={subscribing} className="w-full bg-[#D97736] hover:bg-[#C06528] text-white text-xs h-9" data-testid={`modal-subscribe-${p.id}`}>
                        {tr('Apmaksāt ar karti', 'Pay with Card')}
                      </Button>
                      <Button onClick={() => onWalletSubscribe(p.id)} disabled={subscribing || walletBalance < p.price} variant="outline" className="w-full border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white text-xs h-9" data-testid={`modal-wallet-${p.id}`}>
                        {walletBalance >= p.price ? tr(`Maksāt ar maciņu (€${walletBalance.toFixed(2)})`, `Wallet (€${walletBalance.toFixed(2)})`) : tr(`Maciņš: €${walletBalance.toFixed(2)}`, `Wallet: €${walletBalance.toFixed(2)}`)}
                      </Button>
                    </div>
                    )
                  )}
                  {p.id !== 'free' && (
                    <a href={`/premium-demo/${p.id}`} target="_blank" rel="noreferrer"
                      className="mt-2 w-full inline-flex items-center justify-center gap-1 text-xs font-semibold text-[#4A6B53] hover:text-[#3B5643] border border-[#E5E4E0] rounded-lg h-8"
                      data-testid={`modal-demo-${p.id}`}>
                      <Eye className="w-3.5 h-3.5" /> {tr('Apskatīt demo', 'View demo')}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {tab === 'compare' && (
          <div className="overflow-x-auto" data-testid="plans-compare-content">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2 font-medium text-[#5C5C5C]">{tr('Funkcija', 'Feature')}</th>
                  {PLAN_ORDER.map((id) => (
                    <th key={id} className={`p-2 text-center font-semibold ${currentId === id ? 'text-[#4A6B53]' : 'text-[#121212]'}`}>
                      {PLAN_LABELS[id][language === 'lv' ? 'lv' : 'en']}
                      {currentId === id && <div className="text-[9px] text-[#4A6B53] font-normal">{tr('aktīvs', 'active')}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_MATRIX.map((row, ri) => (
                  <tr key={ri} className="border-t border-[#F3F2EE]" data-testid={`compare-row-${ri}`}>
                    <td className="p-2 text-[#121212]">{row.label[language === 'lv' ? 'lv' : 'en']}</td>
                    {row.vals.map((v, ci) => (
                      <td key={ci} className={`p-2 text-center ${currentId === PLAN_ORDER[ci] ? 'bg-[#4A6B53]/5' : ''}`}>
                        {typeof v === 'boolean'
                          ? (v ? <Check className="w-4 h-4 text-green-600 inline" /> : <X className="w-3.5 h-3.5 text-[#D1D1CE] inline" />)
                          : <span className="font-semibold text-[#121212]">{v}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'guide' && (
          <div className="space-y-4" data-testid="plans-guide-content">
            {['basic', 'premium', 'premium_max', 'premium_ultra'].map((id) => (
              <div key={id} className="p-3 rounded-xl border border-[#F3F2EE] bg-[#FAFAFA]" data-testid={`guide-${id}`}>
                <p className="text-sm font-semibold text-[#121212] mb-2 flex items-center gap-1"><Crown className="w-3.5 h-3.5 text-[#D97736]" /> {PLAN_LABELS[id][language === 'lv' ? 'lv' : 'en']}</p>
                <ol className="text-xs text-[#5C5C5C] space-y-1.5 list-decimal list-inside leading-relaxed">
                  {INSTRUCTIONS[id][language === 'lv' ? 'lv' : 'en'].map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
